import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import api from '../api';
import { useAuth } from '../context/auth';
import { DisplayPost } from '../components/DisplayPost';
import ListDetailView from '../components/ListDetailView';
import CreateList from './CreateList';
import { LoadingText } from '../components/Style';
import SignupCTA, { SignupBar } from '../components/SignupCTA';

var Container = styled.div`
  box-sizing: border-box;
  max-width: 410px;
  margin: 0 auto;
  padding: 2rem 0.75em 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

var ErrorText = styled.p`
  color: tomato;
  font-size: 0.85rem;
`;

function PostDetail() {
  var { postId } = useParams();
  var navigate = useNavigate();
  var location = useLocation();

  var authContext = useAuth();
  var authTokens = authContext.authTokens;
  var isLoggedIn = authTokens?.token;

  var postState = useState(null);
  var post = postState[0];
  var setPost = postState[1];

  var loadingState = useState(true);
  var isLoading = loadingState[0];
  var setIsLoading = loadingState[1];

  var errorState = useState('');
  var error = errorState[0];
  var setError = errorState[1];

  var bookmarkState = useState({});
  var bookmarkedMap = bookmarkState[0];
  var setBookmarkedMap = bookmarkState[1];

  var libraryAddState = useState({});
  var libraryAddMap = libraryAddState[0];
  var setLibraryAddMap = libraryAddState[1];

  var followState = useState({});
  var followingMap = followState[0];
  var setFollowingMap = followState[1];

  var reactionsState = useState({});
  var userReactionsMap = reactionsState[0];
  var setUserReactionsMap = reactionsState[1];

  var commentLikeState = useState({});
  var likedCommentsMap = commentLikeState[0];
  var setLikedCommentsMap = commentLikeState[1];

  var repostState = useState({});
  var repostedMap = repostState[0];
  var setRepostedMap = repostState[1];

  useEffect(
    function () {
      if (post && post.media) {
        var mediaTitle = post.media.song_title || post.media.album_title;
        document.title = post.media.artist + ' - ' + mediaTitle + ' | HeavyRotation';
      } else if (post && post.list) {
        document.title = post.list.title + ' | HeavyRotation';
      }
    },
    [post]
  );

  useEffect(
    function () {
      if (!postId) return;
      // Set when the user leaves (or the id changes) before the post
      // arrives, so a late item redirect cannot replace whatever page they
      // went to with this list.
      var cancelled = false;

      api
        .get('/api/posts/id/' + postId)
        .then(function (result) {
          if (cancelled) return;
          // A list item lives inside its list: its own URL (from a bookmark,
          // a notification or a share) opens the list at that item's card.
          // A comment anchor (a notification about a comment on the item)
          // is kept, and the list page opens that item's comments.
          if (result.data && result.data.parentListId) {
            var anchor =
              location.hash.indexOf('#comment-') === 0 ? location.hash : '#item-' + result.data.id;
            navigate('/post/' + result.data.parentListId + anchor, { replace: true });
            return;
          }
          setPost(result.data);
          setIsLoading(false);
        })
        .catch(function () {
          if (cancelled) return;
          setError('Post not found.');
          setIsLoading(false);
        });

      if (isLoggedIn) {
        api
          .get('/api/bookmarks')
          .then(function (result) {
            var map = {};
            var ids = result.data || [];
            for (var i = 0; i < ids.length; i++) {
              map[ids[i]] = true;
            }
            setBookmarkedMap(map);
          })
          .catch(function () {});

        api
          .get('/api/library-adds')
          .then(function (result) {
            var map = {};
            var ids = result.data || [];
            for (var i = 0; i < ids.length; i++) {
              map[ids[i]] = true;
            }
            setLibraryAddMap(map);
          })
          .catch(function () {});

        api
          .get('/api/follows')
          .then(function (result) {
            var map = {};
            var ids = result.data || [];
            for (var i = 0; i < ids.length; i++) {
              map[ids[i]] = true;
            }
            setFollowingMap(map);
          })
          .catch(function () {});

        api
          .get('/api/post-reactions')
          .then(function (result) {
            var map = {};
            var items = result.data || [];
            for (var i = 0; i < items.length; i++) {
              var pid = items[i].postId;
              if (!map[pid]) map[pid] = [];
              map[pid].push(items[i].emoji);
            }
            setUserReactionsMap(map);
          })
          .catch(function () {});

        api
          .get('/api/comment-likes')
          .then(function (result) {
            var map = {};
            var ids = result.data || [];
            for (var i = 0; i < ids.length; i++) {
              map[ids[i]] = true;
            }
            setLikedCommentsMap(map);
          })
          .catch(function () {});

        api
          .get('/api/reposts')
          .then(function (result) {
            var map = {};
            var ids = result.data || [];
            for (var i = 0; i < ids.length; i++) {
              map[ids[i]] = true;
            }
            setRepostedMap(map);
          })
          .catch(function () {});
      }
      return function () {
        cancelled = true;
      };
    },
    [postId, isLoggedIn]
  );

  // Scroll to comment when navigating from a notification
  useEffect(
    function () {
      if (!post || isLoading) return;
      var hash = location.hash;
      // A list page handles its own anchors (its items, their comments and
      // its own), once its item cards have loaded.
      if (!hash || post.kind === 'list') return;
      // Small delay to let comments render
      var timer = setTimeout(function () {
        var el = document.getElementById(hash.slice(1));
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.style.transition = 'background-color 0.3s';
          el.style.backgroundColor = '#fff9c4';
          setTimeout(function () {
            el.style.backgroundColor = '';
          }, 2000);
        }
      }, 300);
      return function () {
        clearTimeout(timer);
      };
    },
    [post, isLoading, location.hash]
  );

  function handleBookmarkToggle(id, bookmarked) {
    setBookmarkedMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = prev[key];
        }
      }
      next[id] = bookmarked;
      return next;
    });
  }

  function handleReactionToggle(id, emoji, reacted) {
    setUserReactionsMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = (prev[key] || []).slice();
        }
      }
      if (!next[id]) next[id] = [];
      if (reacted) {
        if (next[id].indexOf(emoji) === -1) next[id].push(emoji);
      } else {
        next[id] = next[id].filter(function (e) {
          return e !== emoji;
        });
      }
      return next;
    });
  }

  function handleFollowToggle(userId, following) {
    setFollowingMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = prev[key];
        }
      }
      next[userId] = following;
      return next;
    });
  }

  function handleCommentLikeToggle(commentId, liked) {
    setLikedCommentsMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = prev[key];
        }
      }
      next[commentId] = liked;
      return next;
    });
  }

  function handleRepostToggle(postId, reposted) {
    setRepostedMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = prev[key];
        }
      }
      next[postId] = reposted;
      return next;
    });
  }

  function handlePostDelete() {
    navigate('/');
  }

  if (isLoading) {
    return <LoadingText>Loading...</LoadingText>;
  }

  if (error || !post) {
    return (
      <Container>
        <ErrorText>{error || 'Post not found.'}</ErrorText>
      </Container>
    );
  }

  var isListPost = post.kind === 'list' && post.list;
  var isOwner = isLoggedIn && authTokens?.user?.id === post.user?.id;
  var isUnpublished = post.status === 'unpublished';

  // An author opening their own draft goes straight into the composer: a
  // half-built list has nothing worth reading, and the server only serves it
  // to them anyway.
  if (isListPost && isUnpublished && isOwner) {
    return <CreateList editPost={post} />;
  }

  if (isListPost) {
    return (
      <Container>
        {!isLoggedIn && <SignupBar />}
        <ListDetailView
          post={post}
          isBookmarked={!!bookmarkedMap[post.id]}
          userReactions={userReactionsMap[post.id] || []}
          isAddedToLibrary={!!libraryAddMap[post.id]}
          isFollowing={post.user ? !!followingMap[post.user.id] : false}
          isReposted={!!repostedMap[post.id]}
          onBookmarkToggle={handleBookmarkToggle}
          onReactionToggle={handleReactionToggle}
          onFollowToggle={handleFollowToggle}
          likedCommentsMap={likedCommentsMap}
          onCommentLikeToggle={handleCommentLikeToggle}
          onPostDelete={handlePostDelete}
          onRepostToggle={handleRepostToggle}
          bookmarkedMap={bookmarkedMap}
          userReactionsMap={userReactionsMap}
          libraryAddMap={libraryAddMap}
        />
      </Container>
    );
  }

  return (
    <Container>
      {!isLoggedIn && <SignupCTA style={{ marginBottom: '1rem' }} />}
      <DisplayPost
        post={post}
        isBookmarked={!!bookmarkedMap[post.id]}
        userReactions={userReactionsMap[post.id] || []}
        isAddedToLibrary={!!libraryAddMap[post.id]}
        isFollowing={post.user ? !!followingMap[post.user.id] : false}
        isReposted={!!repostedMap[post.id]}
        onBookmarkToggle={handleBookmarkToggle}
        onReactionToggle={handleReactionToggle}
        onFollowToggle={handleFollowToggle}
        followSource="post_detail"
        likedCommentsMap={likedCommentsMap}
        onCommentLikeToggle={handleCommentLikeToggle}
        onPostDelete={handlePostDelete}
        onRepostToggle={handleRepostToggle}
        fullText
      />
    </Container>
  );
}

export default PostDetail;
